import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
const CSRF_COOKIE_NAME = "passport_csrf";
const NO_REFRESH_PATHS = [
    "/auth/signin",
    "/auth/signup",
    "/auth/verify-email",
    "/auth/google/session",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/refresh",
];

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

function getCookie(name) {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

let refreshPromise = null;

api.interceptors.request.use((config) => {
    config.withCredentials = true;
    const method = String(config.method || "get").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const csrf = getCookie(CSRF_COOKIE_NAME);
        if (csrf) config.headers["X-CSRF-Token"] = csrf;
    }
    // Attach the user's preferred language so backend can localize responses
    const lang = localStorage.getItem("passport_language") || navigator.language?.slice(0, 2) || "en";
    config.headers["Accept-Language"] = lang;
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error?.config || {};
        const status = error?.response?.status;
        const url = original.url || "";
        const shouldSkip = NO_REFRESH_PATHS.some((path) => url.includes(path));
        if (status === 401 && !original._retry && !shouldSkip) {
            original._retry = true;
            try {
                refreshPromise = refreshPromise || api.post("/auth/refresh").finally(() => {
                    refreshPromise = null;
                });
                await refreshPromise;
                return api(original);
            } catch (refreshError) {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("passport-auth-expired"));
                }
            }
        }
        return Promise.reject(error);
    },
);

// Turn a relative /api/uploads/<id> into an absolute URL.
// NOTE: paths starting with /passport- are frontend public-folder assets
// and must NOT be prefixed with BACKEND_URL (FastAPI doesn't serve them).
export const toAbsolute = (u) => {
    if (!u) return u;
    if (/^https?:/.test(u)) return u;
    if (u.startsWith("/passport-")) return u;   // frontend public asset
    return `${BACKEND_URL}${u}`;
};

export const authApi = {
    signup: (data) => api.post("/auth/signup", data).then((r) => r.data),
    signin: (data) => api.post("/auth/signin", data).then((r) => r.data),
    me: () => api.get("/auth/me").then((r) => r.data),
    googleSession: (session_id) => api.post("/auth/google/session", { session_id }).then((r) => r.data),
    verifyEmail: (email, otp) => api.post("/auth/verify-email", { email, otp }).then((r) => r.data),
    resendVerification: (email) => api.post("/auth/resend-verification", { email }).then((r) => r.data),
    forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
    resetPassword: (email, otp, new_password) => api.post("/auth/reset-password", { email, otp, new_password }).then((r) => r.data),
    refresh: () => api.post("/auth/refresh").then((r) => r.data),
    logout: () => api.post("/auth/logout").then((r) => r.data),
    sessions: () => api.get("/auth/sessions").then((r) => r.data),
    revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`).then((r) => r.data),
};

export const feedApi = {
    list: () => api.get("/feed").then((r) => r.data),
    following: () => api.get("/feed/following").then((r) => r.data),
    create: (data) => api.post("/feed", data).then((r) => r.data),
    like: (id) => api.post(`/feed/${id}/like`).then((r) => r.data),
    comments: (id) => api.get(`/feed/${id}/comments`).then((r) => r.data),
    addComment: (id, text) => api.post(`/feed/${id}/comments`, { text }).then((r) => r.data),
};

export const stampsApi = {
    list: () => api.get("/stamps").then((r) => r.data),
    create: (data) => api.post("/stamps", data).then((r) => r.data),
};

export const newsletterApi = { join: (email) => api.post("/newsletter", { email }).then((r) => r.data) };

export const paymentsApi = {
    checkout: (lookup_key) =>
        api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin }).then((r) => r.data),
    status: (session_id) => api.get(`/payments/status/${session_id}`).then((r) => r.data),
};

async function toDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export const uploadsApi = {
    upload: async (file) => {
        const dataUrl = await toDataUrl(file);
        const r = await api.post("/uploads/image", { data_url: dataUrl });
        return toAbsolute(r.data.url);
    },
    uploadMedia: async (file) => {
        const dataUrl = await toDataUrl(file);
        const r = await api.post("/uploads/media", { data_url: dataUrl });
        return { url: toAbsolute(r.data.url), kind: r.data.kind, mime: r.data.mime };
    },
    // Object storage (multipart) — used by the enhanced social post system
    uploadObject: async (file, onProgress) => {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads/object", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
            },
        });
        return {
            id: r.data.id,
            url: toAbsolute(r.data.url),
            raw_url: r.data.url,
            kind: r.data.kind,
            mime: r.data.mime,
            size: r.data.size,
        };
    },
};

export const postsApi = {
    list: (params = {}) => api.get("/posts", { params }).then((r) => r.data),
    get: (id) => api.get(`/posts/${id}`).then((r) => r.data),
    mine: () => api.get("/posts/mine").then((r) => r.data),
    create: (data) => api.post("/posts", data).then((r) => r.data),
    update: (id, data) => api.patch(`/posts/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/posts/${id}`).then((r) => r.data),
    react: (id, type = "love") => api.post(`/posts/${id}/react`, { type }).then((r) => r.data),
    save: (id, collection_id) => api.post(`/posts/${id}/save`, { collection_id }).then((r) => r.data),
    unsave: (id) => api.delete(`/posts/${id}/save`).then((r) => r.data),
    share: (id) => api.post(`/posts/${id}/share`).then((r) => r.data),
    publicPost: (slug) => api.get(`/posts/public/${slug}`).then((r) => r.data),
    comments: (id) => api.get(`/posts/${id}/comments`).then((r) => r.data),
    addComment: (id, text, parent_id) => api.post(`/posts/${id}/comments`, { text, parent_id }).then((r) => r.data),
    editComment: (cid, text) => api.patch(`/comments/${cid}`, { text }).then((r) => r.data),
    removeComment: (cid) => api.delete(`/comments/${cid}`).then((r) => r.data),
    likeComment: (cid) => api.post(`/comments/${cid}/like`).then((r) => r.data),
};

export const collectionsApi = {
    list: () => api.get("/collections").then((r) => r.data),
    create: (data) => api.post("/collections", data).then((r) => r.data),
    remove: (id) => api.delete(`/collections/${id}`).then((r) => r.data),
    posts: (id) => api.get(`/collections/${id}/posts`).then((r) => r.data),
    saved: () => api.get("/saved-posts").then((r) => r.data),
};

export const hashtagsApi = {
    trending: () => api.get("/hashtags/trending").then((r) => r.data),
    detail: (tag) => api.get(`/hashtags/${encodeURIComponent(tag)}`).then((r) => r.data),
};

export const notificationsApi = {
    list: () => api.get("/notifications").then((r) => r.data),
    readAll: () => api.post("/notifications/read-all").then((r) => r.data),
    read: (id) => api.post(`/notifications/${id}/read`).then((r) => r.data),
};

export const reportsApi = {
    submit: (data) => api.post("/reports", data).then((r) => r.data),
};

export const adminApi = {
    stats: () => api.get("/admin/stats").then((r) => r.data),
    reports: (status) => api.get("/admin/reports", { params: status ? { status } : {} }).then((r) => r.data),
    resolve: (id, action, note) => api.post(`/admin/reports/${id}/resolve`, { action, note }).then((r) => r.data),
};

export const mediaApi = {
    mine: () => api.get("/media/mine").then((r) => r.data),
    remove: (id) => api.delete(`/media/${id}`).then((r) => r.data),
};

export const storageApi = {
    access: () => api.get("/storage/access").then((r) => r.data),
    status: () => api.get("/storage/status").then((r) => r.data),
};

export const usersApi = {
    search: (params = {}) => api.get("/users/search", { params: typeof params === "string" ? { q: params } : params }).then((r) => r.data),
    profile: (handle) => api.get(`/users/handle/${handle}`).then((r) => r.data),
    reviews: (handle, params = {}) => api.get(`/users/handle/${handle}/reviews`, { params }).then((r) => r.data),
    travel: (handle, params = {}) => api.get(`/users/handle/${handle}/travel`, { params }).then((r) => r.data),
};



export const itinerariesApi = {
    list: () => api.get("/itineraries").then((r) => r.data),
    save: (data) => api.post("/itineraries", data).then((r) => r.data),
    get: (id) => api.get(`/itineraries/${id}`).then((r) => r.data),
    update: (id, data) => api.patch(`/itineraries/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/itineraries/${id}`).then((r) => r.data),
    share: (id, data) => api.post(`/itineraries/${id}/share`, data).then((r) => r.data),
    chatHistory: (id) => api.get(`/itineraries/${id}/chat`).then((r) => r.data),
};

export const tasteApi = {
    get: () => api.get("/me/taste-profile").then((r) => r.data),
    clear: () => api.delete("/me/taste-profile").then((r) => r.data),
};

export function notificationsStreamUrl() {
    return `${API_BASE}/notifications/stream`;
}

export const prefsApi = {
    get: () => api.get("/account/preferences").then((r) => r.data),
    update: (data) => api.patch("/account/preferences", data).then((r) => r.data),
};

export const languageApi = {
    /** Save preferred language to both backend (via prefsApi) and localStorage */
    set: (langCode) => {
        localStorage.setItem("passport_language", langCode);
        return prefsApi.update({ preferred_language: langCode });
    },
};

export const reviewsApi = {
    list: (params) => api.get("/reviews", { params }).then((r) => r.data),
    mine: () => api.get("/reviews/mine").then((r) => r.data),
    create: (data) => api.post("/reviews", data).then((r) => r.data),
    update: (id, data) => api.put(`/reviews/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/reviews/${id}`).then((r) => r.data),
    helpful: (id) => api.post(`/reviews/${id}/helpful`).then((r) => r.data),
};

export const bucketApi = {
    list: () => api.get("/bucket").then((r) => r.data),
    create: (data) => api.post("/bucket", data).then((r) => r.data),
    toggle: (id) => api.patch(`/bucket/${id}/toggle`).then((r) => r.data),
    remove: (id) => api.delete(`/bucket/${id}`).then((r) => r.data),
};

export const messagesApi = {
    threads: () => api.get("/messages/threads").then((r) => r.data),
    thread: (id) => api.get(`/messages/${id}`).then((r) => r.data),
    send: (recipient_handle, text) => api.post("/messages", { recipient_handle, text }).then((r) => r.data),
    conversations: (params = {}) => api.get("/messages/conversations", { params }).then((r) => r.data),
    conversation: (conversationId) => api.get(`/messages/conversations/${conversationId}`).then((r) => r.data),
    createDirectConversation: (handle) => api.post("/messages/conversations/direct", { handle }).then((r) => r.data),
    createGroupConversation: (payload) => api.post("/messages/conversations/group", payload).then((r) => r.data),
    updateConversation: (conversationId, payload) => api.patch(`/messages/conversations/${conversationId}`, payload).then((r) => r.data),
    deleteConversation: (conversationId) => api.delete(`/messages/conversations/${conversationId}`).then((r) => r.data),
    uploadGroupAvatar: (conversationId, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post(`/messages/conversations/${conversationId}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    inviteMembers: (conversationId, member_handles) => api.post(`/messages/conversations/${conversationId}/invite`, { member_handles }).then((r) => r.data),
    setGroupRole: (conversationId, targetUserId, role) => api.patch(`/messages/conversations/${conversationId}/members/${targetUserId}/role`, { role }).then((r) => r.data),
    removeGroupMember: (conversationId, targetUserId) => api.delete(`/messages/conversations/${conversationId}/members/${targetUserId}`).then((r) => r.data),
    transferOwnership: (conversationId, targetUserId) => api.post(`/messages/conversations/${conversationId}/transfer-ownership`, { target_user_id: targetUserId }).then((r) => r.data),
    leaveGroup: (conversationId) => api.post(`/messages/conversations/${conversationId}/leave`).then((r) => r.data),
    groupInvites: () => api.get("/messages/group-invites").then((r) => r.data),
    acceptInvite: (inviteId) => api.post(`/messages/invites/${inviteId}/accept`).then((r) => r.data),
    declineInvite: (inviteId) => api.post(`/messages/invites/${inviteId}/decline`).then((r) => r.data),
    messages: (conversationId, params = {}) => api.get(`/messages/conversations/${conversationId}/messages`, { params }).then((r) => r.data),
    searchConversation: (conversationId, q, limit = 25) => api.get(`/messages/conversations/${conversationId}/search`, { params: { q, limit } }).then((r) => r.data),
    conversationMedia: (conversationId, params = {}) => api.get(`/messages/conversations/${conversationId}/media`, { params }).then((r) => r.data),
    sendMessage: (conversationId, payload) => api.post(`/messages/conversations/${conversationId}/messages`, payload).then((r) => r.data),
    markRead: (conversationId, last_seq) => api.post(`/messages/conversations/${conversationId}/read`, { last_seq }).then((r) => r.data),
    muteConversation: (conversationId, muted_until = null) => api.post(`/messages/conversations/${conversationId}/mute`, { muted_until }).then((r) => r.data),
    uploadAttachment: (conversationId, file, extras = {}, onUploadProgress) => {
        const fd = new FormData();
        fd.append("conversation_id", conversationId);
        if (extras.kind_hint) fd.append("kind_hint", extras.kind_hint);
        if (extras.duration_ms != null) fd.append("duration_ms", String(extras.duration_ms));
        if (extras.waveform && Array.isArray(extras.waveform) && extras.waveform.length) {
            fd.append("waveform", JSON.stringify(extras.waveform));
        }
        fd.append("file", file);
        return api.post("/messages/attachments", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
            signal: extras.signal,
        }).then((r) => r.data);
    },
    saveMessage: (messageId) => api.post(`/messages/messages/${messageId}/save`).then((r) => r.data),
    unsaveMessage: (messageId) => api.delete(`/messages/messages/${messageId}/save`).then((r) => r.data),
    savedMessages: (params = {}) => api.get("/messages/saved", { params }).then((r) => r.data),
    deleteForMe: (messageId) => api.delete(`/messages/messages/${messageId}`).then((r) => r.data),
    unsendMessage: (messageId) => api.post(`/messages/messages/${messageId}/unsend`).then((r) => r.data),
    reactMessage: (messageId, emoji) => api.post(`/messages/messages/${messageId}/reaction`, { emoji }).then((r) => r.data),
    listControls: () => api.get("/messages/controls").then((r) => r.data),
    blockUser: (handle) => api.post(`/messages/controls/${handle}/block`).then((r) => r.data),
    unblockUser: (handle) => api.delete(`/messages/controls/${handle}/block`).then((r) => r.data),
    restrictUser: (handle) => api.post(`/messages/controls/${handle}/restrict`).then((r) => r.data),
    unrestrictUser: (handle) => api.delete(`/messages/controls/${handle}/restrict`).then((r) => r.data),
    requests: () => api.get("/messages/requests").then((r) => r.data),
    sendRequest: (payload) => api.post("/messages/requests", payload).then((r) => r.data),
    acceptRequest: (requestId) => api.post(`/messages/requests/${requestId}/accept`).then((r) => r.data),
    declineRequest: (requestId) => api.delete(`/messages/requests/${requestId}`).then((r) => r.data),
    sharedPreview: (share_ref, options = {}) => api.post("/messages/shared-preview", { ...share_ref, ...(options.allow_unavailable ? { allow_unavailable: true } : {}) }).then((r) => r.data),
    shareRecipients: (q, limit = 12) => api.get("/messages/share/recipients", { params: { q, limit } }).then((r) => r.data),
    sharePassport: (payload) => api.post("/messages/share", payload).then((r) => r.data),
    wsTicket: () => api.post("/messages/ws-ticket").then((r) => r.data),
};

export const countriesApi = {
    detail: (country) => api.get(`/countries/${encodeURIComponent(country)}`).then((r) => r.data),
};

export const tripsApi = {
    list: () => api.get("/trips").then((r) => r.data),
    get: (id) => api.get(`/trips/${id}`).then((r) => r.data),
    create: (data) => api.post("/trips", data).then((r) => r.data),
    remove: (id) => api.delete(`/trips/${id}`).then((r) => r.data),
};

export const conciergeApi = {
    dashboard: () => api.get("/concierge/dashboard").then((r) => r.data),
    history: () => api.get("/concierge/history").then((r) => r.data),
    historyDetail: (conversation_id) => api.get(`/concierge/history/${conversation_id}`).then((r) => r.data),
    deleteHistory: (conversation_id) => api.delete(`/concierge/history/${conversation_id}`).then((r) => r.data),
    preferences: () => api.get("/concierge/preferences").then((r) => r.data),
    updatePreferences: (data) => api.patch("/concierge/preferences", data).then((r) => r.data),
    resetPreferences: () => api.delete("/concierge/preferences").then((r) => r.data),
    start: (message, history, conversation_id) => api.post("/concierge/start", { message, history, conversation_id }).then((r) => r.data),
    reply: (trip_id, message) => api.post("/concierge/reply", { trip_id, message }).then((r) => r.data),
    discover: (message) => api.post("/concierge/discover", { message }).then((r) => r.data),
    buildFrom: (data) => api.post("/concierge/build-from", data).then((r) => r.data),
    mode: (trip_id, mode) => api.post(`/concierge/trips/${trip_id}/mode`, { mode }).then((r) => r.data),
    listTrips: () => api.get("/concierge/trips").then((r) => r.data),
    getTrip: (trip_id) => api.get(`/concierge/trips/${trip_id}`).then((r) => r.data),
    removeTrip: (trip_id) => api.delete(`/concierge/trips/${trip_id}`).then((r) => r.data),
    addItem: (trip_id, item) => api.post(`/concierge/trips/${trip_id}/items`, item).then((r) => r.data),
    removeItem: (trip_id, item_id) => api.delete(`/concierge/trips/${trip_id}/items/${item_id}`).then((r) => r.data),
    undo: (trip_id) => api.post(`/concierge/trips/${trip_id}/undo`).then((r) => r.data),
    setStatus: (trip_id, status) => api.patch(`/concierge/trips/${trip_id}/status`, { status }).then((r) => r.data),
    duplicateTrip: (trip_id) => api.post(`/concierge/trips/${trip_id}/duplicate`).then((r) => r.data),
    share: (trip_id, enable = true) => api.post(`/concierge/trips/${trip_id}/share`, { enable }).then((r) => r.data),
    publicShare: (slug) => api.get(`/concierge/share/${slug}`).then((r) => r.data),
    shareToFeed: (trip_id, data) => api.post(`/concierge/trips/${trip_id}/share-to-feed`, data || {}).then((r) => r.data),
    saveRecommendation: (recommendation) => api.post("/concierge/recommendations/save", { recommendation }).then((r) => r.data),
    deleteRecommendation: (id) => api.delete(`/concierge/recommendations/${id}`).then((r) => r.data),
    addRecommendationToTrip: (recommendation, trip_id) => api.post("/concierge/recommendations/add-to-trip", { recommendation, trip_id }).then((r) => r.data),
};

export const discoverApi = {
    get: () => api.get("/discover").then((r) => r.data),
    refresh: (segment) => api.post("/discover/refresh", { segment }).then((r) => r.data),
    listTrips: (params = {}) => api.get("/discover/trips", { params }).then((r) => r.data),
    listDates: (params = {}) => api.get("/discover/dates", { params }).then((r) => r.data),
    tripDetail: (id) => api.get(`/discover/trips/${id}`).then((r) => r.data),
    dateDetail: (id) => api.get(`/discover/dates/${id}`).then((r) => r.data),
    countries: (type = "all") => api.get("/discover/filters/countries", { params: { type } }).then((r) => r.data),
    cities: (country_id, type = "all") => api.get("/discover/filters/cities", { params: { country_id, type } }).then((r) => r.data),
    likeTrip: (id, liked) => api.put(`/discover/trips/${id}/like`, { liked }).then((r) => r.data),
    likeDate: (id, liked) => api.put(`/discover/dates/${id}/like`, { liked }).then((r) => r.data),
    rateTrip: (id, rating) => api.put(`/discover/trips/${id}/rating`, { rating }).then((r) => r.data),
    clearTripRating: (id) => api.delete(`/discover/trips/${id}/rating`).then((r) => r.data),
    rateDate: (id, rating) => api.put(`/discover/dates/${id}/rating`, { rating }).then((r) => r.data),
    clearDateRating: (id) => api.delete(`/discover/dates/${id}/rating`).then((r) => r.data),
    addTripToItinerary: (id, idempotency_key) => api.post(`/discover/trips/${id}/add-to-itinerary`, { idempotency_key }).then((r) => r.data),
    addDateToMyDates: (id, idempotency_key) => api.post(`/discover/dates/${id}/add-to-my-dates`, { idempotency_key }).then((r) => r.data),
};

export const pushApi = {
    publicKey: () => api.get("/push/vapid-public-key").then((r) => r.data),
    subscribe: (data) => api.post("/push/subscribe", data).then((r) => r.data),
    unsubscribe: (endpoint) => api.post("/push/unsubscribe", { endpoint }).then((r) => r.data),
    test: () => api.post("/push/test").then((r) => r.data),
};

export const travelDateApi = {
    categories: () => api.get("/traveldate/categories").then((r) => r.data),
    wizardOptions: () => api.get("/traveldate/wizard/options").then((r) => r.data),
    geocode: (q) => api.get("/traveldate/geocode", { params: { q } }).then((r) => r.data),
    reverseGeocode: (lat, lon) => api.get("/traveldate/reverse-geocode", { params: { lat, lon } }).then((r) => r.data),
    eventsConfig: () => api.get("/traveldate/events/config").then((r) => r.data),
    setEventsConfig: (provider, enabled) => api.patch("/traveldate/events/config", { provider, enabled }).then((r) => r.data),
    eventsPreview: (location, style) => api.get("/traveldate/events/preview", { params: { location, style } }).then((r) => r.data),
    search: (data) => api.post("/traveldate/search", data).then((r) => r.data),
    create: (data) => api.post("/traveldate/create", data).then((r) => r.data),
    regenerate: (id) => api.post(`/traveldate/${id}/regenerate`).then((r) => r.data),
    list: (opts = {}) => api.get("/traveldate", { params: opts }).then((r) => r.data),
    history: () => api.get("/traveldate/mine/history").then((r) => r.data),
    get: (id) => api.get(`/traveldate/${id}`).then((r) => r.data),
    remove: (id) => api.delete(`/traveldate/${id}`).then((r) => r.data),
    toggleFavorite: (id) => api.patch(`/traveldate/${id}/favorite`).then((r) => r.data),
    setStatus: (id, status) => api.patch(`/traveldate/${id}/status`, { status }).then((r) => r.data),
    rate: (id, data) => api.patch(`/traveldate/${id}/rate`, data).then((r) => r.data),
    share: (id, enable = true) => api.post(`/traveldate/${id}/share`, { enable }).then((r) => r.data),
    publicShare: (slug) => api.get(`/traveldate/share/${slug}`).then((r) => r.data),
    addToTrip: (id, data = {}) => api.post(`/traveldate/${id}/add-to-trip`, data).then((r) => r.data),
};

export const agentsApi = {
    listAgents: () => api.get("/agents").then((r) => r.data),
    getAgent: (id) => api.get(`/agents/${id}`).then((r) => r.data),
    patchAgent: (id, data) => api.patch(`/agents/${id}`, data).then((r) => r.data),
    pauseAgent: (id) => api.post(`/agents/${id}/pause`).then((r) => r.data),
    activateAgent: (id) => api.post(`/agents/${id}/activate`).then((r) => r.data),
    disableAgent: (id) => api.post(`/agents/${id}/disable`).then((r) => r.data),
    // Support
    createTicket: (message, context) => api.post("/support/tickets", { message, context }).then((r) => r.data),
    replyTicket: (ticket_id, message) => api.post(`/support/tickets/${ticket_id}/message`, { message }).then((r) => r.data),
    listTickets: () => api.get("/support/tickets").then((r) => r.data),
    getTicket: (id) => api.get(`/support/tickets/${id}`).then((r) => r.data),
    // Owner
    ownerOverview: () => api.get("/owner/overview").then((r) => r.data),
    ownerTickets: (opts = {}) => api.get("/owner/tickets", { params: opts }).then((r) => r.data),
    ownerAlerts: (opts = {}) => api.get("/owner/alerts", { params: opts }).then((r) => r.data),
    ackAlert: (id, data) => api.patch(`/owner/alerts/${id}`, data).then((r) => r.data),
    ownerAudit: () => api.get("/owner/audit").then((r) => r.data),
    generateReport: () => api.post("/owner/reports/generate").then((r) => r.data),
    listReports: () => api.get("/owner/reports").then((r) => r.data),
    decideReport: (id, data) => api.patch(`/owner/reports/${id}`, data).then((r) => r.data),
    pauseAll: () => api.post("/owner/pause-all").then((r) => r.data),
    resumeAll: () => api.post("/owner/resume-all").then((r) => r.data),
};

export const followsApi = {
    follow:    (handle) => api.post(`/follows/${handle}`).then((r) => r.data),
    unfollow:  (handle) => api.delete(`/follows/${handle}`).then((r) => r.data),
    mine:      ()       => api.get("/follows/mine").then((r) => r.data),
    status:    (handle) => api.get(`/follows/status/${handle}`).then((r) => r.data),
    followers: (handle) => api.get(`/follows/followers/${handle}`).then((r) => r.data),
    following: (handle) => api.get(`/follows/following/${handle}`).then((r) => r.data),
};


export const friendsApi = {
    request:          (handle) => api.post(`/friends/request/${handle}`).then((r) => r.data),
    accept:           (handle) => api.post(`/friends/accept/${handle}`).then((r) => r.data),
    reject:           (handle) => api.post(`/friends/reject/${handle}`).then((r) => r.data),
    remove:           (handle) => api.delete(`/friends/${handle}`).then((r) => r.data),
    mine:             ()       => api.get("/friends/mine").then((r) => r.data),
    myRequests:       ()       => api.get("/friends/requests").then((r) => r.data),
    status:           (handle) => api.get(`/friends/status/${handle}`).then((r) => r.data),
    listFriends:      (handle) => api.get(`/friends/list/${handle}`).then((r) => r.data),
    getMutualFriends: (handle) => api.get(`/friends/mutual/${handle}`).then((r) => r.data),
};

export const storiesApi = {
    list: () => api.get("/stories").then((r) => r.data),
    create: (data) => api.post("/stories", data).then((r) => r.data),
};

export const reelsApi = {
    list: (params = {}) => api.get("/reels", { params }).then((r) => r.data),
    get: (id) => api.get(`/reels/${id}`).then((r) => r.data),
    related: (id) => api.get(`/reels/${id}/related`).then((r) => r.data),
    create: (data) => api.post("/reels", data).then((r) => r.data),
    update: (id, data) => api.patch(`/reels/${id}`, data).then((r) => r.data),
    remove: (id) => api.delete(`/reels/${id}`).then((r) => r.data),
    removeDraft: (id, data = {}) => api.delete(`/reels/drafts/${id}`, { data }).then((r) => r.data),
    like: (id) => api.post(`/reels/${id}/like`).then((r) => r.data),
    save: (id) => api.post(`/reels/${id}/save`).then((r) => r.data),
    unsave: (id) => api.delete(`/reels/${id}/save`).then((r) => r.data),
    saved: () => api.get("/reels/saved/mine").then((r) => r.data),
    share: (id) => api.post(`/reels/${id}/share`).then((r) => r.data),
    view: (id, data = {}) => api.post(`/reels/${id}/view`, data).then((r) => r.data),
    comments: (id) => api.get(`/reels/${id}/comments`).then((r) => r.data),
    addComment: (id, text, parent_id = null) => api.post(`/reels/${id}/comments`, { text, parent_id }).then((r) => r.data),
    editComment: (id, text) => api.patch(`/reels/comments/${id}`, { text }).then((r) => r.data),
    removeComment: (id) => api.delete(`/reels/comments/${id}`).then((r) => r.data),
    likeComment: (id) => api.post(`/reels/comments/${id}/like`).then((r) => r.data),
};

export const accountApi = {
    exportData: async () => {
        const res = await api.get("/account/export", { responseType: "blob" });
        return res.data; // Blob
    },
    deleteAccount: (payload) => api.delete("/account", { data: payload }).then((r) => r.data),
    publicDeleteRequest: (payload) => api.post("/account/delete-request", payload).then((r) => r.data),
    changePassword: (current_password, new_password) => api.post("/account/change-password", { current_password, new_password }).then((r) => r.data),
    updateHandle: (new_handle) => api.patch("/account/handle", { new_handle }).then((r) => r.data),
    updateProfile: (data) => api.patch("/account/profile", data).then((r) => r.data),
};

export async function* streamAI(path, body) {
    const headers = { "Content-Type": "application/json" };
    const csrf = getCookie(CSRF_COOKIE_NAME);
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        credentials: "include",
    });
    if (!res.ok) {
        let msg = `Request failed: ${res.status}`;
        try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch {}
        throw new Error(msg);
    }
    if (!res.body) return;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        yield decoder.decode(value, { stream: true });
    }
}
